import React, { memo, useEffect, useState } from 'react';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import './UpdateCommandExportMenu.css';

export type UpdateCommandExportSheet = 'updateCommands' | 'executionDetails';

interface UpdateCommandExportMenuProps {
  anchorEl: HTMLElement | null;
  handleClose: () => void;
  handleExport: (fileType: 'csv' | 'xlsx', sheet?: UpdateCommandExportSheet) => void;
}

const CSV_SHEET_OPTIONS: { label: string, sheet: UpdateCommandExportSheet }[] = [
  { label: 'Update Commands', sheet: 'updateCommands' },
  { label: 'Execution Details', sheet: 'executionDetails' },
];

// Excel exports both sheets in a single file, a csv holds a single sheet so the sheet is selected from the secondary menu of To CSV
function UpdateCommandExportMenu({ anchorEl, handleClose, handleExport }: UpdateCommandExportMenuProps) {
  const [csvMenuAnchorEl, setCsvMenuAnchorEl] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (!anchorEl) setCsvMenuAnchorEl(null);
  }, [anchorEl]);

  return (
    <>
      <Menu
        id='update-command-export-option-dropdown'
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        className='export__option__dropdown'
        sx={{ top: '30px' }}
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        <MenuItem
          selected={Boolean(csvMenuAnchorEl)}
          onMouseEnter={(e) => setCsvMenuAnchorEl(e?.currentTarget)}
          onClick={(e) => setCsvMenuAnchorEl(e?.currentTarget)}
        >
          <div className='export__btn-option'>
            To CSV
          </div>
        </MenuItem>
        <MenuItem onMouseEnter={() => setCsvMenuAnchorEl(null)}>
          <div className='export__btn-option' onClick={(e) => {
            e?.stopPropagation();
            handleExport('xlsx');
          }}>
            To Excel
          </div>
        </MenuItem>
      </Menu>

      <Menu
        id='update-command-export-csv-sheet-dropdown'
        anchorEl={csvMenuAnchorEl}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        className='update__command__export__csv__dropdown'
        open={Boolean(csvMenuAnchorEl)}
        onClose={() => setCsvMenuAnchorEl(null)}
        // Secondary menu without backdrop, so the main menu stays usable while it is open
        hideBackdrop
        disableAutoFocus
        disableEnforceFocus
        disableRestoreFocus
        sx={{ pointerEvents: 'none' }}
        slotProps={{ paper: { sx: { pointerEvents: 'auto' } } }}
      >
        {CSV_SHEET_OPTIONS.map((option) => (
          <MenuItem key={option.sheet}>
            <div className='export__btn-option' onClick={(e) => {
              e?.stopPropagation();
              handleExport('csv', option.sheet);
            }}>
              {option.label}
            </div>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

export default memo(UpdateCommandExportMenu);
